import React, { useEffect, useState, useRef, lazy } from 'react';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';
import { CTAButton } from '../ui/CTAButton';
import { ShieldCheckIcon, BrainIcon, ZapIcon, TargetIcon, PlayIcon } from 'lucide-react';
export function HeroSection() {
  const {
    ref,
    isVisible
  } = useScrollAnimation({
    threshold: 0.05
  });
  const [loadVideo, setLoadVideo] = useState(false);
  const videoRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setLoadVideo(true);
        observer.disconnect();
      }
    }, {
      rootMargin: '200px'
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return <section ref={ref} className="relative w-full min-h-screen flex items-center justify-center overflow-hidden" style={{
    background: 'linear-gradient(180deg, #050810 0%, #0a0e1a 40%, #0d1321 100%)'
  }} data-id="element-82">
      {/* Ambient glow */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[600px] pointer-events-none" style={{
      background: 'radial-gradient(ellipse at center, rgba(201,168,76,0.07) 0%, transparent 70%)'
    }} aria-hidden="true" data-id="element-83" />

      {/* Subtle grid pattern */}
      <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{
      backgroundImage: `linear-gradient(rgba(201,168,76,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(201,168,76,0.3) 1px, transparent 1px)`,
      backgroundSize: '60px 60px'
    }} aria-hidden="true" data-id="element-84" />

      <div className="relative z-10 max-w-5xl mx-auto px-5 md:px-8 py-20 md:py-28 text-center" data-id="element-85">
        {/* Badge */}
        <div className={`
            inline-flex items-center gap-2 px-4 py-2 mb-8
            border border-[#c9a84c]/30 rounded-full
            bg-[#c9a84c]/5
            transition-all duration-1000 delay-100
            ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}
          `} data-id="element-86">
          <ShieldCheckIcon className="w-4 h-4 text-[#c9a84c]" aria-hidden="true" data-id="element-87" />
          <span className="text-[#c9a84c] text-sm font-medium tracking-wider uppercase" data-id="element-88">
            Protocolo Científico Verificado
          </span>
        </div>

        {/* Main Headline */}
        <h1 className={`
            font-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl
            font-bold leading-[1.15] mb-8
            text-[#e8e6e1]
            transition-all duration-1000 delay-200
            ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}
          `} data-id="element-89">
          La verdad es que tu problema{' '}
          <span className="text-gold-gradient" data-id="element-90">no es falta de motivación.</span>
          <br className="hidden md:block" data-id="element-91" /> Es que tu identidad actual está{' '}
          <span className="italic" data-id="element-92">programada para sabotearte.</span>
        </h1>

        {/* Subheadline */}
        <p className={`
            text-lg md:text-xl lg:text-2xl text-[#9a9aab] max-w-3xl mx-auto mb-12
            leading-relaxed
            transition-all duration-1000 delay-300
            ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}
          `} data-id="element-93">
          Este protocolo está ayudando a miles de personas a eliminar el
          autosabotaje, restaurar su disciplina y recuperar el control total de
          su mente{' '}
          <span className="text-[#e8e6e1] font-semibold" data-id="element-94">en solo 21 días.</span>
        </p>

        {/* Bullet triggers */}
        <div className={`
            grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl mx-auto mb-14
            transition-all duration-1000 delay-400
            ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}
          `} data-id="element-95">
          <div className="flex items-start gap-3 text-left" data-id="element-96">
            <BrainIcon className="w-5 h-5 text-[#c9a84c] mt-0.5 flex-shrink-0" aria-hidden="true" data-id="element-97" />
            <span className="text-[#9a9aab] text-sm md:text-base" data-id="element-98">
              Si empiezas pero{' '}
              <span className="text-[#e8e6e1]" data-id="element-99">nunca terminas</span>
            </span>
          </div>
          <div className="flex items-start gap-3 text-left" data-id="element-100">
            <TargetIcon className="w-5 h-5 text-[#c9a84c] mt-0.5 flex-shrink-0" aria-hidden="true" data-id="element-101" />
            <span className="text-[#9a9aab] text-sm md:text-base" data-id="element-102">
              Si te sientes <span className="text-[#e8e6e1]" data-id="element-103">estancado</span>
            </span>
          </div>
          <div className="flex items-start gap-3 text-left" data-id="element-104">
            <ZapIcon className="w-5 h-5 text-[#c9a84c] mt-0.5 flex-shrink-0" aria-hidden="true" data-id="element-105" />
            <span className="text-[#9a9aab] text-sm md:text-base" data-id="element-106">
              Si sabes que tienes{' '}
              <span className="text-[#e8e6e1]" data-id="element-107">más potencial</span> pero no
              logras activarlo
            </span>
          </div>
          <div className="flex items-start gap-3 text-left" data-id="element-108">
            <ShieldCheckIcon className="w-5 h-5 text-[#c9a84c] mt-0.5 flex-shrink-0" aria-hidden="true" data-id="element-109" />
            <span className="text-[#9a9aab] text-sm md:text-base" data-id="element-110">
              Este sistema fue diseñado para{' '}
              <span className="text-[#e8e6e1]" data-id="element-111">reprogramar tu identidad</span>
            </span>
          </div>
        </div>

        {/* CTA */}
        <div className={`
            flex flex-col items-center gap-4
            transition-all duration-1000 delay-500
            ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}
          `} data-id="element-112">
          <CTAButton size="large" data-id="element-113">
            Acceder al protocolo completo ahora
          </CTAButton>
          <p className="text-[#6b6b7b] text-sm tracking-wide" data-id="element-114">
            Acceso inmediato · Disponible por tiempo limitado
          </p>
        </div>

        {/* Video embed */}
        <div ref={videoRef} className={`
            mt-14 mb-2 flex justify-center
            transition-all duration-1000 delay-600
            ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}
          `} data-id="element-115">
          <div className="w-full max-w-[280px] sm:max-w-[320px] rounded-2xl overflow-hidden border border-[#c9a84c]/20 shadow-[0_0_40px_rgba(201,168,76,0.1)]" data-id="element-116">
            <div className="relative w-full" style={{
            paddingBottom: '177.78%'
          }} data-id="element-117">
              {loadVideo ? <iframe className="absolute inset-0 w-full h-full" src="https://www.youtube.com/embed/y79XZcSYHg0" title="Sistema de Reprogramación Mental Espiritual" frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen loading="lazy" data-id="element-118" /> : <button onClick={() => setLoadVideo(true)} className="absolute inset-0 w-full h-full bg-[#0a0e1a] flex flex-col items-center justify-center gap-4 cursor-pointer border-0 group" aria-label="Reproducir video" data-id="element-119">
                  <div className="w-16 h-16 rounded-full bg-[#c9a84c]/20 flex items-center justify-center group-hover:bg-[#c9a84c]/30 transition-colors" data-id="element-120">
                    <PlayIcon className="w-7 h-7 text-[#c9a84c] ml-1" fill="currentColor" data-id="element-121" />
                  </div>
                  <span className="text-[#9a9aab] text-sm" data-id="element-122">
                    Reproducir video
                  </span>
                </button>}
            </div>
          </div>
        </div>

        {/* Social proof micro */}
        <div className={`
            mt-16 flex flex-col sm:flex-row items-center justify-center gap-6 sm:gap-10
            transition-all duration-1000 delay-700
            ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}
          `} data-id="element-123">
          <div className="flex items-center gap-2" data-id="element-124">
            <div className="flex -space-x-2" data-id="element-125">
              {[1, 2, 3, 4, 5].map(i => <div key={i} className="w-8 h-8 rounded-full border-2 border-[#0a0e1a] bg-gradient-to-br from-[#252b45] to-[#1a1f36]" aria-hidden="true" data-id="element-126" />)}
            </div>
            <span className="text-[#9a9aab] text-sm" data-id="element-127">
              +2,400 personas transformadas
            </span>
          </div>
          <div className="flex items-center gap-1" data-id="element-128">
            {[1, 2, 3, 4, 5].map(i => <svg key={i} className="w-4 h-4 text-[#c9a84c]" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true" data-id="element-129">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" data-id="element-130" />
              </svg>)}
            <span className="text-[#9a9aab] text-sm ml-1" data-id="element-131">
              4.9/5 satisfacción
            </span>
          </div>
        </div>
      </div>

      {/* Bottom fade */}
      <div className="absolute bottom-0 left-0 right-0 h-32 pointer-events-none" style={{
      background: 'linear-gradient(to top, #0a0e1a, transparent)'
    }} aria-hidden="true" data-id="element-132" />
    </section>;
}